import { useState, useEffect } from 'react';
import { CheckCircle2, AlertTriangle, Clock, Truck } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';

const badge = (s) => {
  const m = { 'Awaiting Confirmation': 'bg-gold/15 text-gold-dark', 'Confirmed': 'bg-forest/10 text-forest', 'Partially Confirmed': 'bg-forest/10 text-forest', 'Shortage Reported': 'bg-red-50 text-red-600', 'Picked Up': 'bg-dark/10 text-dark' };
  return m[s] || 'bg-dark/10 text-dark';
};

export default function FarmOrders() {
  const [orders, setOrders] = useState([]);
  const [shortageOrder, setShortageOrder] = useState(null);
  const [sf, setSf] = useState({ product_id: '', quantity_available: '', reason: '', substitute: '', notes: '' });

  const load = () => api.get('/farm/orders').then((r) => setOrders(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const confirm = async (id, partial) => { await api.post(`/farm/orders/${id}/confirm`, { partial }); toast.success(partial ? 'Partial order confirmed' : 'Order confirmed'); load(); };
  const ready = async (id) => { await api.post(`/farm/orders/${id}/ready`); toast.success('Marked ready for pickup'); load(); };

  const submitShortage = async () => {
    try {
      await api.post(`/farm/orders/${shortageOrder}/shortage`, { ...sf, quantity_available: sf.quantity_available === '' ? null : parseFloat(sf.quantity_available) });
      toast.success('Shortage reported to FarmBox'); setShortageOrder(null); setSf({ product_id: '', quantity_available: '', reason: '', substitute: '', notes: '' }); load();
    } catch { toast.error('Failed'); }
  };

  return (
    <div data-testid="farm-orders">
      <p className="font-manrope text-dark/60 mb-6">Confirm the quantities you can supply, or report a shortage. FarmBox handles all customer changes.</p>
      {orders.length === 0 ? (
        <div className="bg-white rounded-2xl border border-forest/10 p-12 text-center font-manrope text-dark/50">No FarmBox orders yet.</div>
      ) : (
        <div className="space-y-5">
          {orders.map((o) => (
            <div key={o.order_id} className="bg-white rounded-2xl border border-forest/10 p-6" data-testid={`order-${o.order_id}`}>
              <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                <div><h3 className="font-playfair text-xl font-bold text-dark">Week of {o.week}</h3><p className="font-manrope text-xs text-dark/50">Order {o.order_id}</p></div>
                <span className={`px-3 py-1 rounded-full text-xs font-bold ${badge(o.status)}`}>{o.status}</span>
              </div>
              <div className="space-y-1 mb-4">
                {o.items.map((it) => (
                  <div key={it.product_id} className="flex justify-between py-2 border-b border-forest/5 font-manrope text-sm">
                    <span className="text-dark">{it.name}</span>
                    <span className="text-dark/70">{it.requested_quantity} {it.unit} · ${it.partner_price}/unit · <strong>${it.line_total.toFixed(2)}</strong></span>
                  </div>
                ))}
              </div>
              <div className="flex flex-wrap items-center justify-between gap-4">
                <div className="font-manrope text-sm text-dark/60">
                  <span className="font-playfair text-lg font-bold text-forest">Total ${o.total.toFixed(2)}</span>
                  {o.pickup_date && <span className="ml-3">Pickup {o.pickup_date} · {o.pickup_window}</span>}
                </div>
                <div className="flex flex-wrap gap-2">
                  {o.status === 'Awaiting Confirmation' && <>
                    <button onClick={() => confirm(o.order_id, false)} className="btn-primary py-2 px-4 text-sm" data-testid={`confirm-full-${o.order_id}`}><CheckCircle2 className="w-4 h-4 mr-1" />Confirm Full</button>
                    <button onClick={() => confirm(o.order_id, true)} className="btn-secondary py-2 px-4 text-sm" data-testid={`confirm-partial-${o.order_id}`}>Confirm Partial</button>
                    <button onClick={() => setShortageOrder(o.order_id)} className="py-2 px-4 text-sm rounded-full border-2 border-red-200 text-red-600 font-manrope font-bold hover:bg-red-50" data-testid={`shortage-${o.order_id}`}><AlertTriangle className="w-4 h-4 mr-1 inline" />Report Shortage</button>
                  </>}
                  {['Confirmed', 'Partially Confirmed'].includes(o.status) && o.pickup_status !== 'Ready for Pickup' && o.pickup_status !== 'Picked Up' && (
                    <button onClick={() => ready(o.order_id)} className="btn-primary py-2 px-4 text-sm" data-testid={`ready-${o.order_id}`}><Truck className="w-4 h-4 mr-1" />Mark Ready for Pickup</button>
                  )}
                  {o.pickup_status === 'Ready for Pickup' && <span className="flex items-center gap-1 text-forest font-manrope text-sm font-semibold"><Clock className="w-4 h-4" />Ready for pickup</span>}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={!!shortageOrder} onOpenChange={(v) => !v && setShortageOrder(null)}>
        <DialogContent className="bg-cream">
          <DialogHeader><DialogTitle className="font-playfair text-2xl">Report Shortage</DialogTitle><DialogDescription>Tell FarmBox what you can supply and why.</DialogDescription></DialogHeader>
          <div className="space-y-4">
            <div><Label>Product</Label><Input className="bg-white border-forest/20" placeholder="Product name" value={sf.product_id} onChange={(e) => setSf({ ...sf, product_id: e.target.value })} data-testid="shortage-product" /></div>
            <div><Label>Quantity available</Label><Input type="number" className="bg-white border-forest/20" value={sf.quantity_available} onChange={(e) => setSf({ ...sf, quantity_available: e.target.value })} data-testid="shortage-qty" /></div>
            <div><Label>Reason</Label><Input className="bg-white border-forest/20" value={sf.reason} onChange={(e) => setSf({ ...sf, reason: e.target.value })} /></div>
            <div><Label>Suggested substitute</Label><Input className="bg-white border-forest/20" value={sf.substitute} onChange={(e) => setSf({ ...sf, substitute: e.target.value })} /></div>
            <div><Label>Notes</Label><Textarea className="bg-white border-forest/20" value={sf.notes} onChange={(e) => setSf({ ...sf, notes: e.target.value })} /></div>
            <button onClick={submitShortage} className="btn-primary w-full justify-center" data-testid="submit-shortage">Send to FarmBox</button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
